import { z } from 'zod';

import { ACCEPT_ATTACH_DOCUMENT_FILE_TYPES, AVERAGE_SCORE_TEMPLATES } from '../../constants';

const ATTACH_DOCUMENT_MAX_UPLOAD_SIZE_IN_MB = 20;
const ATTACH_DOCUMENT_MAX_UPLOAD_SIZE = ATTACH_DOCUMENT_MAX_UPLOAD_SIZE_IN_MB * 1024 * 1024; // in bytes

const signedUrlSchema = z.object({
  name: z.string(),
  uploadFileName: z.string(),
});

const BaseDuringAndPostExamSettingsSectionFormSchema = z.object({
  questionnaire: z.object({
    enabled: z.enum(['true', 'false']),
    templateId: z.enum(['template-a', 'template-b', 'template-c']).nullable().optional(),
  }),
  resultDisplay: z.object({
    shouldDisplay: z.enum(['true', 'false']),
  }),
  resultSend: z.object({
    portal: z.enum(['true', 'false']),
  }),
  certificate: z.object({
    shouldIssue: z.enum(['true', 'false']),
    template: z.object({
      passTemplate: z.enum(
        ['average-score-by-part', 'average-score-by-subject', 'no-score', 'section-score', ''],
        {
          message: 'กรุณาเลือก Certificate Template',
        },
      ),
      failTemplate: z.enum(
        ['average-score-by-part', 'average-score-by-subject', 'no-score', 'section-score', ''],
        {
          message: 'กรุณาเลือก Certificate Template',
        },
      ),
    }),
    deliveryTime: z.object({
      type: z.enum(['immediately', 'delay']).optional(),
      delay: z
        .union([z.number(), z.literal('')])
        .optional()
        .describe('Number of days after exam'),
    }),
    averageScoreCondition: z.enum(['all-centers', 'each-center']).optional(),
    expiry: z.object({
      type: z.enum(['none', 'delay']).optional(),
      delay: z
        .union([z.number(), z.literal('')])
        .optional()
        .describe('Number of years after passing exam'),
    }),
  }),
  attachDocument: z.object({
    enabled: z.enum(['true', 'false']),
    file: z.union([
      z.instanceof(File, {
        message: 'กรุณาแนบไฟล์อัปโหลด',
      }),
      signedUrlSchema,
      z.null(),
    ]),
  }),
});

function addValidationIssue(ctx: z.RefinementCtx, path: (string | number)[], message: string) {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path,
    message,
  });
}

function validateRequiredFields(
  data: z.infer<typeof BaseDuringAndPostExamSettingsSectionFormSchema>,
  ctx: z.RefinementCtx,
) {
  if (data.questionnaire.enabled === 'true' && !data.questionnaire.templateId) {
    addValidationIssue(ctx, ['questionnaire', 'templateId'], 'กรุณาเลือก Questionnaire Template');
  }

  if (data.certificate.shouldIssue !== 'true') return;

  if (!data.certificate.template.passTemplate) {
    addValidationIssue(
      ctx,
      ['certificate', 'template', 'passTemplate'],
      'กรุณาเลือก Certificate Template',
    );
  }

  if (!data.certificate.template.failTemplate) {
    addValidationIssue(
      ctx,
      ['certificate', 'template', 'failTemplate'],
      'กรุณาเลือก Certificate Template',
    );
  }

  if (!data.certificate.deliveryTime.type) {
    addValidationIssue(
      ctx,
      ['certificate', 'deliveryTime', 'type'],
      'กรุณาเลือกระยะเวลาที่ส่ง Certificate',
    );
  }

  if (!data.certificate.expiry.type) {
    addValidationIssue(ctx, ['certificate', 'expiry', 'type'], 'กรุณาเลือกอายุของใบรับรอง');
  }
}

function validateCertificateDeliveryTime(
  data: z.infer<typeof BaseDuringAndPostExamSettingsSectionFormSchema>,
  ctx: z.RefinementCtx,
) {
  if (data.certificate.deliveryTime.type !== 'delay') return;

  if (!data.certificate.deliveryTime.delay) {
    addValidationIssue(
      ctx,
      ['certificate', 'deliveryTime', 'type'],
      'ระยะเวลาส่ง Certificate ต้องมากกว่า 0 วัน',
    );
    addValidationIssue(
      ctx,
      ['certificate', 'deliveryTime', 'delay'],
      '', // NOTE: This is just to show red border without message
    );
  }

  if (data.certificate?.deliveryTime?.delay && data.certificate?.deliveryTime?.delay > 30) {
    addValidationIssue(
      ctx,
      ['certificate', 'deliveryTime', 'type'],
      'ระยะเวลาส่ง Certificate ต้องไม่เกิน 30 วัน',
    );
    addValidationIssue(
      ctx,
      ['certificate', 'deliveryTime', 'delay'],
      '', // NOTE: This is just to show red border without message
    );
  }
}

// Validate expiry constraints
function validateCertificateExpiry(
  data: z.infer<typeof BaseDuringAndPostExamSettingsSectionFormSchema>,
  ctx: z.RefinementCtx,
) {
  if (data.certificate.expiry.type !== 'delay') return;

  if (!data.certificate.expiry.delay) {
    addValidationIssue(ctx, ['certificate', 'expiry', 'type'], 'อายุของใบรับรองต้องมากกว่า 0 ปี');
    addValidationIssue(
      ctx,
      ['certificate', 'expiry', 'delay'],
      '', // NOTE: This is just to show red border without message
    );
  }

  if (data.certificate?.expiry?.delay && data.certificate?.expiry?.delay > 10) {
    addValidationIssue(ctx, ['certificate', 'expiry', 'type'], 'อายุของใบรับรองต้องไม่เกิน 10 ปี');
    addValidationIssue(
      ctx,
      ['certificate', 'expiry', 'delay'],
      '', // NOTE: This is just to show red border without message
    );
  }
}

// Validate average score condition
function validateCertificateAverageScoreCondition(
  data: z.infer<typeof BaseDuringAndPostExamSettingsSectionFormSchema>,
  ctx: z.RefinementCtx,
) {
  const hasAverageScoreTemplate =
    AVERAGE_SCORE_TEMPLATES.includes(data.certificate.template.passTemplate) ||
    AVERAGE_SCORE_TEMPLATES.includes(data.certificate.template.failTemplate);

  if (!data.certificate.averageScoreCondition && hasAverageScoreTemplate) {
    addValidationIssue(
      ctx,
      ['certificate', 'averageScoreCondition'],
      'กรุณาเลือกเงื่อนไขการคำนวณคะแนนเฉลี่ย',
    );
  }
}

// Validate attach document constraints
function validateAttachDocument(
  data: z.infer<typeof BaseDuringAndPostExamSettingsSectionFormSchema>,
  ctx: z.RefinementCtx,
) {
  if (data.attachDocument.enabled === 'false') return;

  if (!data.attachDocument.file || data.attachDocument.file === null) {
    addValidationIssue(ctx, ['attachDocument', 'file'], 'กรุณาแนบไฟล์อัปโหลด');
    return;
  }

  const file = data.attachDocument.file;
  const isFileObject = file instanceof File;

  if (isFileObject && !ACCEPT_ATTACH_DOCUMENT_FILE_TYPES.includes(file.type)) {
    addValidationIssue(
      ctx,
      ['attachDocument', 'file'],
      'รูปแบบไฟล์ไม่ถูกต้อง กรุณาอัปโหลดไฟล์ .pdf เท่านั้น',
    );
  }

  if (isFileObject && file.size > ATTACH_DOCUMENT_MAX_UPLOAD_SIZE) {
    addValidationIssue(
      ctx,
      ['attachDocument', 'file'],
      `ขนาดไฟล์ต้องไม่เกิน ${ATTACH_DOCUMENT_MAX_UPLOAD_SIZE_IN_MB} MB`,
    );
  }
}

export const DuringAndPostExamSettingsSectionFormSchema =
  BaseDuringAndPostExamSettingsSectionFormSchema.superRefine((data, ctx) => {
    validateRequiredFields(data, ctx);
    validateCertificateDeliveryTime(data, ctx);
    validateCertificateExpiry(data, ctx);
    validateCertificateAverageScoreCondition(data, ctx);
    validateAttachDocument(data, ctx);
  });
